import { createValidator, passengers as validatePassengers } from 'utils/formValidations';
import purchaserValidations from 'utils/purchaserValidations';

// Buyer contact/identity fields worth reporting. Kept explicit (rather than spreading the whole
// purchase) so the report never grows silently when unrelated purchase fields are added.
const BUYER_FIELDS = [
  'purchaserFirstName',
  'purchaserLastName',
  'purchaserSecondLastName',
  'email',
  'phone',
  'phoneCountry',
  'phoneCode',
  'documentType',
  'documentId',
];

// Mirrors the fields the passenger validator can report, so a missing field in `missingFields`
// always has its stored counterpart here. `document` is deliberately excluded: it is not a field
// value but the uploaded file itself, carried as a `<meta>::<base64>` string (see
// `documentWithoutSize`), so reporting it would ship a scan of someone's ID in every event. A
// missing `passenger_<n>.document` still shows up in `missingFields`.
const PASSENGER_FIELDS = [
  'firstName',
  'lastName',
  'secondLastName',
  'email',
  'phone',
  'phoneCountry',
  'phoneCode',
  'documentType',
  'documentId',
  'documentTypeSecond',
  'documentIdSecond',
  'dateOfBirth',
  'nationality',
  'gender',
];

/**
 * Returns true when a value is missing (null, empty, or whitespace-only).
 * @param {*} value - Value to check.
 * @returns {boolean} Whether the value is blank.
 */
const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

/**
 * Picks a fixed set of keys from an object, skipping blanks so the report shows only real values.
 * @param {object} source - Object to read from.
 * @param {Array<string>} keys - Keys to pick.
 * @returns {object} The picked subset.
 */
const pickPresent = (source = {}, keys) =>
  keys.reduce((acc, key) => {
    if (!isBlank(source?.[key])) acc[key] = source[key];
    return acc;
  }, {});

/**
 * Builds the data snapshot reported when a Quick Purchase payment attempt is made.
 *
 * Required-field rules are not restated here: the buyer runs through the same
 * `purchaserValidations` the purchaser form uses and the passengers through the same form-level
 * `passengers` validator, both of which read the tenant flags from the store. That keeps the report
 * aligned with what actually blocks payment instead of drifting from it.
 *
 * `missingFields` answers "what is absent from the purchase we are about to pay for", while
 * `unsavedBuyerFields` answers "what did the user fill in that the purchase never received" — the
 * distinction that separates a user who skipped a field from a persistence bug.
 * @param {object} params - Builder parameters.
 * @param {object} params.purchase - Purchase state (the data the payment will be made against).
 * @param {object} [params.purchaserFormValues] - Live purchaser (buyer) form values.
 * @returns {{
 *   missingFields: Array<string>,
 *   unsavedBuyerFields: Array<string>,
 *   hasIncompleteData: boolean,
 *   buyer: object,
 *   buyerForm: object,
 *   passengers: Array<object>,
 * }} The snapshot.
 */
export default function buildPaymentAttemptDataAudit({ purchase = {}, purchaserFormValues = {} }) {
  const purchasePassengers = Array.isArray(purchase?.passengers) ? purchase.passengers : [];

  // The buyer is validated against the purchase, since that is what the payment is made against.
  const buyerErrors = createValidator(purchaserValidations)(purchase || {});
  const missingFields = Object.keys(buyerErrors)
    // legalTerms/taxpayer fields are not part of the buyer contact data this report is about.
    .filter((field) => BUYER_FIELDS.includes(field))
    .map((field) => `buyer.${field}`);

  const passengerErrors = validatePassengers(purchasePassengers);
  if (Array.isArray(passengerErrors)) {
    passengerErrors.forEach((error, index) => {
      if (!error || typeof error !== 'object') return;
      Object.keys(error).forEach((field) => {
        if (error[field]) missingFields.push(`passenger_${index + 1}.${field}`);
      });
    });
  }

  // Filled in the form, absent from the purchase: the signature of a buyer that was never updated.
  const unsavedBuyerFields = BUYER_FIELDS.filter(
    (field) => !isBlank(purchaserFormValues?.[field]) && isBlank(purchase?.[field]),
  ).map((field) => `buyer.${field}`);

  return {
    missingFields,
    unsavedBuyerFields,
    hasIncompleteData: missingFields.length > 0 || unsavedBuyerFields.length > 0,
    buyer: pickPresent(purchase, BUYER_FIELDS),
    buyerForm: pickPresent(purchaserFormValues, BUYER_FIELDS),
    passengers: purchasePassengers.map((passenger) => pickPresent(passenger, PASSENGER_FIELDS)),
  };
}
